import { useState } from 'react';
import { useQueryPromise, fetchPostQuery, apiCall } from '/src/api';
import {
    googleAddToCart,
    googleRemoveFromCart,
    facebookAddToCart,
    justunoUpdateCart,
    gtpAddToCart,
    convertAddToCart,
} from '/src/services';
import graphQLQueries from './graph-ql-queries';

const getLocalCartId = () => localStorage.getItem('cart_id');
const setLocalCartId = id => localStorage.setItem('cart_id', id);
const removeLocalCartId = () => localStorage.removeItem('cart_id');

const useCart = () => {
    const {
        data: cart,
        isLoading: isLoadingCart,
        runQueryPromise,
        setValues,
    } = useQueryPromise({ data: {} });
    const [isLoadingAmazonExpressConfig, setIsLoadingAmazonExpressConfig] = useState(false);

    const clearCart = () => {
        setValues({ data: {} });
        removeLocalCartId();
    };

    const getCart = cartId => runQueryPromise({
        query: graphQLQueries.getCart,
        variables: { cartId },
        queryKey: ['cart'],
    });

    const getCustomerCart = () => runQueryPromise({
        query: graphQLQueries.getCustomerCart,
        queryKey: ['customerCart'],
    });

    const analyticsAddedProducts = (cartItems, addedItems, cart) => {
        googleAddToCart({ cartItems, addedItems, cart });
        facebookAddToCart({ cartItems, addedItems });
        gtpAddToCart({ cartItems, addedItems, cart });
        convertAddToCart(addedItems);
    };

    const analyticsRemoveProducts = (cartItems, cartItemId) => {
        const { product: { sku, name, price_range }, quantity } = cartItems.find(({ id }) => id === cartItemId);

        googleRemoveFromCart({ sku, name, price_range, quantity });
    };

    const analyticsUpdateCart = cart => {
        justunoUpdateCart(cart);
    };

    const addProductsToCart = ({ isAuth, cartId, ...variables }) => {
        const { cartItems } = variables;

        if (!cartId) {
            setValues({ isLoading: true });

            return fetchPostQuery({
                query: isAuth
                    ? graphQLQueries.getCustomerCart
                    : graphQLQueries.createEmptyCart,
            }).then(response => {
                const { customerCart, createEmptyCart } = response;

                if (customerCart) {
                    setValues({ data: customerCart });
                } else {
                    setLocalCartId(createEmptyCart)
                }

                return fetchPostQuery({
                    query: graphQLQueries.addProductsToCart,
                    variables: {
                        ...variables,
                        cartId: customerCart
                            ? customerCart.id
                            : createEmptyCart
                    },
                })
            }).then(response => {
                const {
                    addProductsToCart: {
                        cart,
                        cart: { itemsV2: { items } },
                        user_errors,
                    }
                } = response;

                setValues({ data: cart, isLoading: false });

                if (!!user_errors?.length) {
                    throw user_errors;
                }

                analyticsAddedProducts(items, cartItems, cart);
                analyticsUpdateCart(cart);

                return response;
            }).catch(errors => {
                setValues({ isLoading: false });

                throw errors;
            })
        }

        return runQueryPromise({
            query: graphQLQueries.addProductsToCart,
            queryKey: ['addProductsToCart', 'cart'],
            variables: { ...variables, cartId },
            isMethodPost: true,
        }).then(response => {
            const {
                addProductsToCart: {
                    cart,
                    cart: { itemsV2: { items } },
                    user_errors,
                }
            } = response;

            if (!!user_errors?.length) {
                throw user_errors;
            }

            analyticsAddedProducts(items, cartItems, cart);
            analyticsUpdateCart(cart);

            return response;
        });
    };

    const removeItemFromCart = variables => {
        const { input: { cart_item_id } } = variables;
        const { itemsV2: { items } } = cart;

        return runQueryPromise({
            query: graphQLQueries.removeItemFromCart,
            queryKey: ['removeItemFromCart', 'cart'],
            variables,
            isMethodPost: true,
        }).then(response => {
            const { removeItemFromCart: { cart } } = response;
            analyticsRemoveProducts(items, cart_item_id);
            analyticsUpdateCart(cart);

            return response;
        });
    };

    const updateCartItems = variables => runQueryPromise({
        query: graphQLQueries.updateCartItems,
        queryKey: ['updateCartItems', 'cart'],
        variables,
        isMethodPost: true,
    }).then(response => {
        const { updateCartItems: { cart } } = response;
        analyticsUpdateCart(cart);

        return response;
    });

    const reOrder = variables => runQueryPromise({
        query: graphQLQueries.reOrder,
        queryKey: ['reorderItems', 'cart'],
        variables,
        isMethodPost: true,
    });

    const applyGiftCardToCart = variables => runQueryPromise({
        query: graphQLQueries.applyGiftCardToCart,
        queryKey: ['applyGiftCardToCart', 'cart'],
        variables,
        isMethodPost: true,
    });

    const removeGiftCardFromCart = variables => runQueryPromise({
        query: graphQLQueries.removeGiftCardFromCart,
        queryKey: ['removeGiftCardFromCart', 'cart'],
        variables,
        isMethodPost: true,
    });

    const applyCouponToCart = variables => runQueryPromise({
        query: graphQLQueries.applyCouponToCart,
        queryKey: ['applyCouponToCart', 'cart'],
        variables,
        isMethodPost: true,
    });

    const removeCouponFromCart = variables => runQueryPromise({
        query: graphQLQueries.removeCouponFromCart,
        queryKey: ['removeCouponFromCart', 'cart'],
        variables,
        isMethodPost: true,
    });

    const mergeCarts = variables => runQueryPromise({
        query: graphQLQueries.mergeCarts,
        queryKey: ['mergeCarts'],
        variables,
        isMethodPost: true,
    });

    const setGuestEmailOnCart = variables => runQueryPromise({
        query: graphQLQueries.setGuestEmailOnCart,
        queryKey: ['setGuestEmailOnCart', 'cart'],
        variables,
        isMethodPost: true,
    });

    const setShippingAddressesOnCart = variables => runQueryPromise({
        query: graphQLQueries.setShippingAddressesOnCart,
        queryKey: ['setShippingAddressesOnCart', 'cart'],
        variables,
        isMethodPost: true,
    });

    const setBillingAddressOnCart = variables => runQueryPromise({
        query: graphQLQueries.setBillingAddressOnCart,
        queryKey: ['setBillingAddressOnCart', 'cart'],
        variables,
        isMethodPost: true,
    });

    const setShippingMethodsOnCart = variables => runQueryPromise({
        query: graphQLQueries.setShippingMethodsOnCart,
        queryKey: ['setShippingMethodsOnCart', 'cart'],
        variables,
        isMethodPost: true,
    });

    const setPaymentMethodOnCart = variables => runQueryPromise({
        query: graphQLQueries.setPaymentMethodOnCart,
        queryKey: ['setPaymentMethodOnCart', 'cart'],
        variables,
        isMethodPost: true,
    });

    const updateRewardPoints = variables => runQueryPromise({
        query: graphQLQueries.updateRewardPoints,
        queryKey: ['updateRewardPoints', 'cart'],
        variables,
        isMethodPost: true,
    });

    const placeOrder = variables => {
        setValues({ isLoading: true });

        return fetchPostQuery({
            query: graphQLQueries.placeOrder,
            variables,
        }).then(({ placeOrder }) => {
            const { errors } = placeOrder;

            if (errors?.length) {
                throw errors
            }

            setValues({ isLoading: false });

            return placeOrder;
        }).catch(errors => {
            setValues({ isLoading: false });

            throw errors;
        })
    };

    const setIsLoadingCart = isLoading => setValues({ isLoading });

    const sendQuery = (query, variables) => {
        setValues({ isLoading: true });

        return fetchPostQuery({
            query,
            variables,
        }).then(response => {
            setValues({ isLoading: false });

            return response;
        }).catch(errors => {
            setValues({ isLoading: false });

            throw errors;
        })
    };

    const sendApi = (url , variables) => {
        setValues({ isLoading: true });

        return apiCall({
            url,
            method: 'POST',
            body: variables,
        }).then(response => {
            setValues({ isLoading: false });

            return response;
        }).catch(errors => {
            setValues({ isLoading: false });

            throw  errors;
        })
    };

    // const getStripeClientSecret = variables => sendQuery(graphQLQueries.getStripeRequiresAction, variables).then(({ getStripeRequiresAction }) => getStripeRequiresAction);
    const getStripeClientSecret = variables => sendApi('/rest/V1/stripe/payments/get_requires_action', variables);

    // const completeStripePayment = variables => sendQuery(graphQLQueries.completeStripePayment, variables).then(({ completeStripePayment }) => completeStripePayment);
    const completeStripePayment = variables => sendApi('/rest/V1/stripe/payments/complete_payment', variables);

    const getCardnetPaymentStatus = variables => sendQuery(graphQLQueries.getCardnetPaymentStatus, variables).then(({ getCardnetPaymentStatus }) => getCardnetPaymentStatus);

    const processCardnetPaymentSecureChallenge = variables => sendQuery(graphQLQueries.processCardnetPaymentSecureChallenge, variables).then(({ processCardnetPaymentSecureChallenge }) => processCardnetPaymentSecureChallenge);

    const restoreQuote = variables => sendQuery(graphQLQueries.restoreQuote, variables).then(({ restoreQuote }) => restoreQuote);

    const createPaymentOrder = variables => sendQuery(graphQLQueries.createPaymentOrder, variables).then(({ createPaymentOrder }) => createPaymentOrder);

    const amazonPayConfig = variables => sendQuery(graphQLQueries.amazonPayConfig, variables).then(({ checkoutSessionConfig }) => checkoutSessionConfig);

    const amazonExpressConfig = variables => {
        setIsLoadingAmazonExpressConfig(true);

        return fetchPostQuery({
            query: graphQLQueries.amazonExpressConfig,
            variables,
        }).then(({ checkoutSessionConfig }) => {
            setIsLoadingAmazonExpressConfig(false);

            return checkoutSessionConfig;
        }).catch(errors => {
            setIsLoadingAmazonExpressConfig(false);

            throw errors;
        })
    };

    const amazonExpressPay = variables => sendQuery(graphQLQueries.amazonExpressPay, variables).then(({ updateCheckoutSession }) => updateCheckoutSession);

    const generateExtendedHash = variables => sendQuery(graphQLQueries.generateExtendedHash, variables).then(({ generateExtendedHash }) => generateExtendedHash);

    return {
        cart,
        isCart: !!cart.id,
        isCartItems: !!cart.total_quantity,
        isLoadingCart: isLoadingCart || isLoadingAmazonExpressConfig,
        clearCart,
        getCart,
        getCustomerCart,
        addProductsToCart,
        removeItemFromCart,
        getLocalCartId,
        removeLocalCartId,
        reOrder,
        updateCartItems,
        applyGiftCardToCart,
        removeGiftCardFromCart,
        applyCouponToCart,
        removeCouponFromCart,
        mergeCarts,
        setGuestEmailOnCart,
        setShippingAddressesOnCart,
        setBillingAddressOnCart,
        setShippingMethodsOnCart,
        setPaymentMethodOnCart,
        placeOrder,
        setIsLoadingCart,
        getStripeClientSecret,
        completeStripePayment,
        getCardnetPaymentStatus,
        processCardnetPaymentSecureChallenge,
        restoreQuote,
        createPaymentOrder,
        amazonPayConfig,
        amazonExpressConfig,
        amazonExpressPay,
        updateRewardPoints,
        generateExtendedHash,
    }
};

export default useCart;

